import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { base64ToArrayBuffer } from './base64ToArrayBuffer.util';
import { assuranceExtract as base64Docx } from './assuranceExtract.template';
import ImageModule from 'docxtemplater-image-module-free/build/imagemodule';
import LinkModule from 'docxtemplater-link-module-free/build/linkmodule';
import { DOMParser, XMLSerializer } from '@xmldom/xmldom'; // New imports
import { findFrameworkName } from './findFrameworkName.util';

function fixFrameworkNames(data) {
    if (data.controls && data.controls.length > 0) {
        for (let i = 0; i < data.controls.length; i++) {
            if (data.controls[i].controlID) {
                data.controls[i].controlFramework = findFrameworkName(data.controls[i].controlID);
            }
        }
    }

    if (data.standardsImplemented && data.standardsImplemented.length > 0) {
        for (let i = 0; i < data.standardsImplemented.length; i++) {
            if (data.standardsImplemented[i].framework) {
                data.standardsImplemented[i].framework = findFrameworkName(data.standardsImplemented[i].framework, true);
            }
        }
    }

    return data;
}


export async function generateDocument(options, saveFile = false) {
    // Load the template document
    const content = base64ToArrayBuffer(base64Docx);

    // Configure the image module
    const imageModule = new ImageModule({
        centered: true,
        getImage: function (tagValue, tagName) {
            // tagValue is expected to be a base64 data URL or a URL
            return fetch(tagValue)
                .then((res) => {
                    if (!res.ok) throw new Error(`Request to ${tagValue} failed, status: ${res.status}`);
                    return res.arrayBuffer();
                });
        },
        getSize: async function (img, tagValue, tagName) {
            try {
                const src = typeof tagValue === 'string' ? tagValue : '';
                const dims = await new Promise((resolve) => {
                    const im = new Image();
                    im.onload = () => resolve({ w: im.naturalWidth || im.width, h: im.naturalHeight || im.height });
                    im.onerror = () => resolve({ w: 600, h: 600 });
                    im.src = src;
                });
                const maxWidth = 600;
                const ratio = dims.w ? Math.min(1, maxWidth / dims.w) : 1;
                return [Math.round(dims.w * ratio), Math.round(dims.h * ratio)];
            } catch (e) {
                console.error('Error reading image size:', e, { tagValue, tagName });
                return [600, 600];
            }
        }
    });

    // Configure the hyperlink module
    // const hyperlinkModule = new LinkModule();

    // Create a zip instance for reading the docx file
    const zip = new PizZip(content);
    const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [imageModule]
    });

    // Prepare the data
    let data = {
        ExportDate: new Date(),
        CompanyLegalName: options.CompanyLegalName,
        CompanyFriendlyName: options.CompanyFriendlyName,
        standardsImplemented: options.standardsImplemented,
        controls: options.controls,
        assuranceTasks: options.assuranceTasks,
    };

    data = fixFrameworkNames(data);

    console.log('Data to use to generate assurance doc', data)

    // Render the document
    await doc.resolveData(data);
    try {
        doc.render();
    } catch (error) {
        console.error('Error rendering document:', error);
        throw error;
    }

    // After rendering, process the document to replace placeholders with hyperlinks
    const updatedZip = processHyperlinks(doc.getZip());

    // Generate the document
    const buf = updatedZip.generate({ type: 'blob' });

    // Generate the document
    // const buf = doc.getZip().generate({ type: 'blob' });

    if (saveFile) {
        // Save the document using FileSaver.js
        return await saveAs(buf, `Assurance Docs Export.docx`);
    }

    return buf;
}


function getHttpData(url, callback) {
    fetch(url)
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Request to ${url} failed, status code: ${response.status}`);
            }
            return response.arrayBuffer();
        })
        .then((buf) => callback(null, buf))
        .catch((e) => callback(e));
}

function processHyperlinks(zip) {
    // Read the document.xml file
    let docXmlStr = zip.file('word/document.xml').asText();
    // Read the relationships file
    let relsXmlStr = zip.file('word/_rels/document.xml.rels').asText();

    // Parse the XML content
    const parser = new DOMParser();
    const serializer = new XMLSerializer();
    const docXml = parser.parseFromString(docXmlStr, 'application/xml');
    const relsXml = parser.parseFromString(relsXmlStr, 'application/xml');

    // Find the highest existing relationship ID
    const relationships = relsXml.getElementsByTagName('Relationship');
    let maxRelId = 1;
    for (let i = 0; i < relationships.length; i++) {
        const relId = relationships[i].getAttribute('Id');
        const idNum = parseInt(relId.replace('rId', ''), 10);
        if (idNum >= maxRelId) {
            maxRelId = idNum + 1;
        }
    }
    let nextRelId = maxRelId;

    // Process the document to replace placeholders with hyperlinks
    const body = docXml.getElementsByTagName('w:body')[0];
    traverseNodes(body, (textNodes) => {
        const { fullText, textNodeInfos } = getFullTextAndInfos(textNodes);
        const regex = /\[text:(.*?)\]\(href:(.*?)\)/g;
        let match;
        let lastIndex = 0;

        const newNodes = [];

        while ((match = regex.exec(fullText)) !== null) {
            const beforeText = fullText.substring(lastIndex, match.index);
            if (beforeText) {
                const beforeRuns = createRunsFromText(docXml, beforeText, textNodeInfos, lastIndex, match.index);
                newNodes.push(...beforeRuns);
            }

            // Create hyperlink node
            const relId = `rId${nextRelId++}`;
            const hyperlinkNode = createHyperlinkNode(docXml, match[1], relId, textNodeInfos[match.index]);
            newNodes.push(hyperlinkNode);

            // Add relationship
            const newRel = createRelationshipNode(docXml, relId, match[2]);
            relsXml.documentElement.appendChild(newRel);

            lastIndex = regex.lastIndex;
        }

        const afterText = fullText.substring(lastIndex);
        if (afterText) {
            const afterRuns = createRunsFromText(docXml, afterText, textNodeInfos, lastIndex, fullText.length);
            newNodes.push(...afterRuns);
        }

        // Replace the old runs with new nodes
        if (newNodes.length > 0) {
            const parent = textNodes[0].parentNode.parentNode; // w:p
            // Remove old runs
            const runsToRemove = [];
            for (let i = 0; i < parent.childNodes.length; i++) {
                const child = parent.childNodes[i];
                if (child.nodeName === 'w:r') {
                    runsToRemove.push(child);
                }
            }
            runsToRemove.forEach(run => parent.removeChild(run));

            // Append new nodes
            newNodes.forEach(node => parent.appendChild(node));
        }
    });

    // Serialize the modified XML content
    docXmlStr = serializer.serializeToString(docXml);
    relsXmlStr = serializer.serializeToString(relsXml);

    // Update the zip with modified XML content
    zip.file('word/document.xml', docXmlStr);
    zip.file('word/_rels/document.xml.rels', relsXmlStr);

    return zip;
}

function traverseNodes(node, callback) {
    if (!node || !node.nodeName) {
        return;
    }

    if (node.nodeName === 'w:p') {
        const textNodes = [];
        // Collect all text nodes within the paragraph
        if (node.childNodes && node.childNodes.length > 0) {
            for (let i = 0; i < node.childNodes.length; i++) {
                const child = node.childNodes[i];
                if (child.nodeName === 'w:r') {
                    const tNode = child.getElementsByTagName('w:t')[0];
                    if (tNode) {
                        textNodes.push(tNode);
                    }
                }
            }
            if (textNodes.length > 0) {
                callback(textNodes);
            }
        }
    }

    // Recurse into child nodes if any
    if (node.childNodes && node.childNodes.length > 0) {
        for (let i = 0; i < node.childNodes.length; i++) {
            traverseNodes(node.childNodes[i], callback);
        }
    }
}

function getFullTextAndInfos(textNodes) {
    let fullText = '';
    const textNodeInfos = []; // Array of { index: position in fullText, runProperties: w:rPr node }
    let currentIndex = 0;

    textNodes.forEach(tNode => {
        const text = tNode.textContent;
        fullText += text;
        const runNode = tNode.parentNode;
        const runProperties = getRunProperties(runNode);
        for (let i = 0; i < text.length; i++) {
            textNodeInfos[currentIndex + i] = runProperties;
        }
        currentIndex += text.length;
    });

    return { fullText, textNodeInfos };
}

function getRunProperties(runNode) {
    const runPropertiesNode = runNode.getElementsByTagName('w:rPr')[0];
    return runPropertiesNode ? runPropertiesNode.cloneNode(true) : null;
}

function createRunsFromText(docXml, text, textNodeInfos, startIndex, endIndex) {
    const runs = [];
    let currentRunText = '';
    let currentRunProperties = textNodeInfos[startIndex];

    for (let i = 0; i < text.length; i++) {
        const charIndex = startIndex + i;
        const char = text[i];
        const charProperties = textNodeInfos[charIndex];

        if (charProperties !== currentRunProperties) {
            // Create a run with the accumulated text and properties
            const runNode = createTextRunNode(docXml, currentRunText, currentRunProperties);
            runs.push(runNode);
            // Reset for the new run
            currentRunText = char;
            currentRunProperties = charProperties;
        } else {
            currentRunText += char;
        }
    }

    // Add the last run
    if (currentRunText) {
        const runNode = createTextRunNode(docXml, currentRunText, currentRunProperties);
        runs.push(runNode);
    }

    return runs;
}

function createTextRunNode(docXml, text, runProperties) {
    const w_r = docXml.createElement('w:r');
    if (runProperties) {
        w_r.appendChild(runProperties.cloneNode(true));
    }
    const w_t = docXml.createElement('w:t');
    w_t.setAttribute('xml:space', 'preserve');
    w_t.textContent = text;
    w_r.appendChild(w_t);
    return w_r;
}

function createHyperlinkNode(docXml, text, relId, runProperties) {
    const w_hyperlink = docXml.createElement('w:hyperlink');
    w_hyperlink.setAttribute('r:id', relId);

    const w_r = docXml.createElement('w:r');
    const w_rPr = docXml.createElement('w:rPr');

    // Copy existing run properties if any
    if (runProperties) {
        for (let i = 0; i < runProperties.childNodes.length; i++) {
            w_rPr.appendChild(runProperties.childNodes[i].cloneNode(true));
        }
    }

    // Add hyperlink style
    const w_rStyle = docXml.createElement('w:rStyle');
    w_rStyle.setAttribute('w:val', 'Hyperlink');
    w_rPr.appendChild(w_rStyle);

    w_r.appendChild(w_rPr);

    const w_t = docXml.createElement('w:t');
    w_t.setAttribute('xml:space', 'preserve');
    w_t.textContent = text;
    w_r.appendChild(w_t);

    w_hyperlink.appendChild(w_r);

    return w_hyperlink;
}

function createRelationshipNode(docXml, relId, href) {
    const rel = docXml.createElement('Relationship');
    rel.setAttribute('Id', relId);
    rel.setAttribute('Type', 'http://schemas.openxmlformats.org/officeDocument/2006/relationships/hyperlink');
    rel.setAttribute('Target', href);
    rel.setAttribute('TargetMode', 'External');
    return rel;
}